import { apiGet, apiPost, apiDelete } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Admin lookups — drives the Lookups admin page (Platform → Lookups).
// Three concepts, all surfaced here:
//   1. Scenario categories — true lookup table aivalidation.ScenarioCategory
//      with composite (Domain, Code) key + soft-retire (IsActive=false)
//   2. Agent categories     — ai.AiAgentCategory; CRUD via the existing
//      BaseSqlCrudApiController (search/add/update/remove)
//   3. Skill categories     — derived from distinct ai.AiSkill.Category
//      values (it's a free-string column, not a lookup table). We expose
//      a usage list and a rename-across-rows tool.
// ─────────────────────────────────────────────────────────────────────────────

// ── Scenario categories ─────────────────────────────────────────────────────

export interface ScenarioCategoryRow {
  domain: string;
  code: string;
  displayName: string;
  description?: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface UpsertScenarioCategoryRequest {
  domain: string;
  code: string;
  displayName: string;
  description?: string | null;
  displayOrder: number;
  isActive?: boolean;
}

export async function listScenarioCategoriesAdmin(): Promise<ScenarioCategoryRow[]> {
  const wrapped = await apiGet<OperationResult<ScenarioCategoryRow[]>>(
    '/api/Scenarios/Categories',
    { includeRetired: true },
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load scenario categories');
  return wrapped.data ?? [];
}

export async function upsertScenarioCategory(
  body: UpsertScenarioCategoryRequest,
): Promise<ScenarioCategoryRow> {
  const wrapped = await apiPost<OperationResult<ScenarioCategoryRow>>(
    '/api/Scenarios/Categories',
    body,
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to save scenario category');
  }
  return wrapped.data;
}

export async function retireScenarioCategory(
  domain: string,
  code: string,
): Promise<void> {
  const wrapped = await apiDelete<OperationResult<unknown>>(
    `/api/Scenarios/Categories/${encodeURIComponent(domain)}/${encodeURIComponent(code)}`,
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to retire scenario category');
}

// ── Agent categories ────────────────────────────────────────────────────────

export interface AgentCategoryRow {
  id: number;
  name: string;
  description?: string | null;
  /** ai.AiAgentCategory.DisplayOrder (camelCased on the wire). */
  displayOrder?: number | null;
  parentCategoryId?: number | null;
  color?: string | null;
  icon?: string | null;
  isActive: boolean;
  createdDate?: string | null;
  lastUpdatedDate?: string | null;
}

export interface UpsertAgentCategoryRequest {
  id?: number;
  name: string;
  description?: string | null;
  displayOrder?: number | null;
  parentCategoryId?: number | null;
  color?: string | null;
  icon?: string | null;
  isActive?: boolean;
}

export async function listAgentCategories(): Promise<AgentCategoryRow[]> {
  const wrapped = await apiGet<OperationResult<unknown>>(
    '/api/AiAgentCategory/Search',
    { pageSize: 500 },
  );
  if (!wrapped.isSuccess || wrapped.data == null) {
    throw new Error(wrapped.message || 'Failed to load agent categories');
  }
  // BaseSqlCrudApiController.GetSearch wraps results in PagedResult<T>:
  //   { data: T[], paging: { pageNo, pageSize, totalRecordCount, pageCount } }
  // After axios's PascalCase→camelCase pass, that's `data.data`. Some
  // older callers also accept a bare array; keep both paths.
  const payload = wrapped.data as unknown;
  if (Array.isArray(payload)) return payload as AgentCategoryRow[];
  const paged = payload as { data?: AgentCategoryRow[]; items?: AgentCategoryRow[] };
  return paged.data ?? paged.items ?? [];
}

export async function addAgentCategory(body: UpsertAgentCategoryRequest): Promise<AgentCategoryRow> {
  const wrapped = await apiPost<OperationResult<AgentCategoryRow>>(
    '/api/AiAgentCategory/Add',
    { ...body, isActive: body.isActive ?? true },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to create agent category');
  }
  return wrapped.data;
}

export async function updateAgentCategory(body: UpsertAgentCategoryRequest): Promise<AgentCategoryRow> {
  const wrapped = await apiPost<OperationResult<AgentCategoryRow>>(
    '/api/AiAgentCategory/Update',
    body,
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to update agent category');
  }
  return wrapped.data;
}

export async function removeAgentCategory(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult<unknown>>(`/api/AiAgentCategory/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to retire agent category');
}

// ── Skill categories (derived) ──────────────────────────────────────────────

export interface SkillCategoryUsage {
  category: string;
  skillCount: number;
  activeCount: number;
}

export async function listSkillCategoryUsage(): Promise<SkillCategoryUsage[]> {
  const wrapped = await apiGet<OperationResult<SkillCategoryUsage[]>>('/api/AiSkill/Categories');
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load skill categories');
  return wrapped.data ?? [];
}

export async function renameSkillCategory(
  fromCategory: string,
  toCategory: string,
): Promise<{ affected: number }> {
  const wrapped = await apiPost<OperationResult<{ affected: number }>>(
    '/api/AiSkill/Categories/Rename',
    { fromCategory, toCategory },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to rename category');
  }
  return wrapped.data;
}
